import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import Landing from "@/pages/Landing";
import LandingAr from "@/pages/LandingAr";

// ─────────────────────────────────────────────────────────────────
// Prototype D — Cinematic Header Navigation
//
// The photograph IS the navigation item. Each destination renders
// as a compact, full-bleed authentic CrewZNatioN photograph with a
// short label overlaid at the lower edge. The full frame is the
// clickable target. No separate thumbnail-and-caption stack.
//
// Constraints observed:
//   • Black header, single-row desktop structure, wordmark position,
//     language switcher, navigation order preserved.
//   • Header height 112px — inside the 104-120px band.
//   • Panels 115×68px — inside the 105-125 / 62-72 target ranges.
//   • Gaps narrow (6px), no rotation, no mosaic, no scatter.
//   • Hover: brightness lift, minimal 1.03 scale, gold border ring.
//   • Homepage below unchanged. English route renders Landing.
//     Arabic route renders LandingAr (the /ar homepage).
//   • Mobile drawer text-only, unchanged from existing pattern.
// ─────────────────────────────────────────────────────────────────

// Working label set — temporary prototype captions only.
// Prototypes are not editorially locked (§45).
const CINEMATIC_NAV = [
    {
        to: "/",
        labelEn: "HOME",
        labelAr: "الرئيسية",
        // Arrival image DISTINCT from the hero photograph below —
        // wide-body Supra commanding the crowd, strong silhouette
        // recognisable at compact size.
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/5n6pde8o_FB21FACC-7058-44F0-AC7A-FA1B1432698C.JPG",
        end: true,
    },
    {
        to: "/about",
        labelEn: "CrewZNatioN",
        labelAr: "CrewZNatioN",
        // People + machines under lights — community, not parked cars.
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/u65gd357_IMG-20260503-WA0051.jpg",
        protectedEn: true,
    },
    {
        to: "/gathering",
        labelEn: "The Gathering",
        labelAr: "The Gathering",
        // Iconic "Not Tokyo — This is Riyadh"
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/tf8y44s5_F3209F41-0B30-4C13-8807-7FCBDFB18ABD.PNG",
        protectedEn: true,
    },
    {
        to: "/events",
        labelEn: "EVENTS",
        labelAr: "الفعاليات",
        // Corvette trio under festival lights — visible gathering activity
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/l3h9h8v0_35B1CA2D-6A44-433F-A0BB-19EE971F256E.JPEG",
    },
    {
        to: "/gallery",
        labelEn: "MOMENTS",
        labelAr: "اللحظات",
        // "1958 in full bloom — purple lowrider with roses" — distinctive
        // silhouette that survives at small size.
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/eyed6v8w_E4BE464A-17CA-400C-9126-D68CE352C4E2.JPG",
    },
    {
        to: "/sponsors",
        labelEn: "PARTNERS",
        labelAr: "الشراكات",
        image: null, // IMAGE REQUIRED — no authentic partner activation available
    },
    {
        to: "/store",
        labelEn: "GEAR",
        labelAr: "GEAR",
        image: null, // IMAGE REQUIRED — no authentic CrewZNatioN merchandise photograph available
        protectedEn: true,
    },
    {
        to: "/contact",
        labelEn: "WELCOME",
        labelAr: "حياك",
        image: null, // IMAGE REQUIRED — no genuine human invitation/hospitality moment available
    },
];

// Render label token with LTR isolation for protected institutional
// English assets when the header is in Arabic RTL context. Matches
// existing Navbar/NavbarAr treatment for "The Gathering", etc.
function renderLabel(item, isAr) {
    const label = isAr ? item.labelAr : item.labelEn;
    if (item.labelEn === "The Gathering") {
        return (
            <span dir="ltr" className="inline-block">
                The <span className="text-[var(--crewz-gold)]">Gathering</span>
            </span>
        );
    }
    if (item.protectedEn && isAr) {
        return (
            <span dir="ltr" className="inline-block">
                {item.labelEn}
            </span>
        );
    }
    return label;
}

// A single cinematic panel — full-bleed authentic photograph, short
// label overlaid inside at the lower edge, subtle bottom gradient
// only where required for label legibility.
function CinematicPanel({ item, isAr }) {
    const useArabicFont = isAr && !item.protectedEn && item.labelEn !== "The Gathering";
    return (
        <NavLink
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
                `cinematic-panel group relative block overflow-hidden shrink-0 ring-1 transition-shadow duration-300 ${
                    isActive
                        ? "ring-[var(--crewz-gold)]"
                        : "ring-white/10 hover:ring-white/40"
                }`
            }
            style={{ width: "115px", height: "68px" }}
            data-testid={`cinematic-nav-${item.labelEn.toLowerCase().replace(/\s|&/g, "-")}`}
        >
            {({ isActive }) => (
                <>
                    {item.image ? (
                        <img
                            src={item.image}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.035]"
                            draggable={false}
                        />
                    ) : (
                        // IMAGE REQUIRED — neutral black panel, no generic
                        // substitution, no icon, no illustration.
                        <div
                            className="absolute inset-0 flex items-center justify-center bg-[#0f0f0f] border border-dashed border-white/12"
                            aria-hidden="true"
                        >
                            <span className="text-[8px] tracking-[0.22em] uppercase text-white/40 font-body">
                                image required
                            </span>
                        </div>
                    )}

                    {/* Subtle brightness lift on hover — restrained. */}
                    <div
                        className="absolute inset-0 bg-black/20 group-hover:bg-black/5 transition-colors duration-500 pointer-events-none"
                        aria-hidden="true"
                    />

                    {/* Restrained lower gradient — only where the label sits */}
                    {item.image && (
                        <div
                            className="absolute inset-x-0 bottom-0 h-8 pointer-events-none"
                            style={{
                                background:
                                    "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 55%, transparent 100%)",
                            }}
                            aria-hidden="true"
                        />
                    )}

                    {/* Label — overlaid inside the photograph, near the
                        lower edge, subordinate to the image. */}
                    <div
                        className={`absolute inset-x-0 bottom-1.5 px-2 flex items-baseline ${
                            isAr ? "justify-end" : "justify-start"
                        }`}
                    >
                        <span
                            className={`leading-none whitespace-nowrap transition-colors duration-300 ${
                                useArabicFont
                                    ? "font-arabic text-[11px] font-bold"
                                    : "font-body uppercase tracking-[0.14em] text-[9px] font-bold"
                            } ${
                                isActive
                                    ? "text-[var(--crewz-gold)]"
                                    : "text-white/95 group-hover:text-white"
                            }`}
                            style={{ textShadow: "0 1px 2px rgba(0,0,0,0.65)" }}
                        >
                            {renderLabel(item, isAr)}
                        </span>
                    </div>
                </>
            )}
        </NavLink>
    );
}

// The cinematic header shell — same structural container as the
// existing site header; only the item bodies change.
function CinematicHeaderNav({ isAr, toggleTo }) {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className="sticky top-0 z-50 bg-[#111111]/92 backdrop-blur-xl border-b border-white/5"
            data-testid="site-navbar-cinematic"
            dir={isAr ? "rtl" : "ltr"}
        >
            <div
                className="px-6 md:px-10 lg:px-14 flex items-center justify-between"
                style={{ height: "112px" }}
            >
                <nav
                    className={`hidden lg:flex flex-1 items-center gap-1.5 order-2 ${
                        isAr ? "lg:ml-6 xl:ml-8" : "lg:mr-6 xl:mr-8"
                    }`}
                    aria-label="Primary cinematic navigation"
                    style={{ justifyContent: "space-between" }}
                >
                    <div className="flex items-center gap-1.5">
                        {CINEMATIC_NAV.map((item) => (
                            <CinematicPanel key={item.to} item={item} isAr={isAr} />
                        ))}
                    </div>
                    <Link
                        to={toggleTo}
                        className={
                            isAr
                                ? "font-body uppercase tracking-[0.2em] px-2 py-2 text-[12px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                                : "font-arabic normal-case tracking-normal px-2 py-2 text-[14px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                        }
                        dir={isAr ? "ltr" : "rtl"}
                        data-testid="cinematic-nav-lang-switcher"
                    >
                        {isAr ? "English" : "العربية"}
                    </Link>
                </nav>

                <Link
                    to={isAr ? "/prototypes/cinematic-header?lang=ar" : "/prototypes/cinematic-header"}
                    className="flex items-center gap-3 order-1 lg:order-4"
                    data-testid="cinematic-nav-logo"
                >
                    <img
                        src={LOGO_URL}
                        alt="CrewZNatioN"
                        className="h-14 md:h-16 w-auto select-none"
                        draggable={false}
                    />
                </Link>

                <button
                    className="lg:hidden text-white p-2 order-2"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="cinematic-nav-mobile-toggle"
                    aria-label={isAr ? "فتح القائمة" : "Toggle menu"}
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    className="lg:hidden border-t border-white/5 bg-[#111111]/95 backdrop-blur-xl"
                    data-testid="cinematic-nav-mobile-menu"
                >
                    <div className={`px-6 py-6 flex flex-col gap-1 ${isAr ? "font-arabic" : "font-body"}`}>
                        {CINEMATIC_NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `px-2 py-3 text-xs tracking-[0.18em] border-b border-white/5 ${
                                        isActive ? "text-[var(--crewz-gold)]" : "text-zinc-300"
                                    }`
                                }
                            >
                                {renderLabel(item, isAr)}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}

export default function CinematicHeaderPrototype() {
    const location = useLocation();
    const isAr = location.search.includes("lang=ar");
    const toggleTo = isAr
        ? "/prototypes/cinematic-header"
        : "/prototypes/cinematic-header?lang=ar";

    return (
        <div
            className="cinematic-header-prototype min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]"
            data-testid="cinematic-header-prototype"
            dir={isAr ? "rtl" : "ltr"}
        >
            <CinematicHeaderNav isAr={isAr} toggleTo={toggleTo} />

            {/* English route → Landing (English homepage).
                Arabic route → LandingAr (Arabic /ar homepage). */}
            <div className="cinematic-header-body">
                {isAr ? <LandingAr /> : <Landing />}
            </div>

            {/* Scoped rule — hides the embedded navbars that Landing
                and LandingAr mount internally so the cinematic header
                is the only header rendered on this route. Cannot
                escape .cinematic-header-prototype. */}
            <style>{`
                .cinematic-header-prototype .cinematic-header-body [data-testid="site-navbar"],
                .cinematic-header-prototype .cinematic-header-body [data-testid="site-navbar-ar"] {
                    display: none !important;
                }
            `}</style>
        </div>
    );
}
